// Vue
import { Ref, ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { ClassificationCodesType, ClassificationObjectClassType } from "../types/ClassificationCodesType";

// GraphQL
import { GetClassificationCodesQuery } from "@/graphql";

// i18n — the selected "Category Language" is i18n's locale (set by the ProfileCard language picker).
import i18n from "@/services/i18n";

interface ClassificationCodesProvider {
  classificationObjectClass: Ref<ClassificationObjectClassType>;
  // Getters
  getClassificationCodes: () => ClassificationCodesType;
  // Resolve a code's display label by the preferred Category Language (falls back to englishLabel/label/code).
  resolveLabel: (code: ClassificationCodesType[number] | undefined) => string;
  getCodeLabel: (codeValue: string) => string;

  // Methods
  mapData: (data: GetClassificationCodesQuery) => void;

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useClassificationCodes = defineStore("ClassificationCodesProvider", (): ClassificationCodesProvider => {
  // ///////////////////
  // Refs
  // ///////////////////

  const classificationCodes = ref<ClassificationCodesType>([]);
  const classificationObjectClass = ref<ClassificationObjectClassType>([]);

  // ///////////////////
  // Getters
  // ///////////////////

  function getClassificationCodes() {
    return classificationCodes.value;
  }

  // Resolve a code's label by the selected Category Language (i18n's locale, set by the ProfileCard
  // language picker), then fall back to the English label, the base label, and finally the raw code.
  function resolveLabel(code: ClassificationCodesType[number] | undefined): string {
    if (!code) return "";
    const selected = i18n.global.locale.value;
    const hit = (code.translation ?? [])
      .find((entry) => entry.languageCode?.toLowerCase() === selected?.toLowerCase())
      ?.label?.trim();
    if (hit) return hit;
    return code.englishLabel?.trim() || code.label?.trim() || code.code || "";
  }

  function getCodeLabel(codeValue: string): string {
    if (!codeValue) return "";
    return resolveLabel(classificationCodes.value.find((entry) => entry.code === codeValue)) || codeValue;
  }

  // ///////////////////
  // Setters
  // ///////////////////

  function setClassificationCodes(newModels: ClassificationCodesType) {
    classificationCodes.value = newModels;
  }

  // ///////////////////
  // Methods
  // ///////////////////

  function mapData(data: GetClassificationCodesQuery) {
    const { classificationCode, classificationObjectClass: obj } = data;
    // Map all models
    if (!classificationCode) return;
    setClassificationCodes(classificationCode);
    classificationObjectClass.value = obj ?? [];
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const loading = ref<boolean>(false);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // ///////////////////
  // Returns
  // ///////////////////

  return {
    classificationObjectClass,
    // Getters
    getClassificationCodes,
    resolveLabel,
    getCodeLabel,

    // Methods
    mapData,

    // Utility
    getLoading,
    setLoading,
  };
});

export default useClassificationCodes;
